import React, { FC, useEffect, useState } from 'react';
import styles from './AllProducts.module.scss';
import ProductsService from 'services/ProductsService/products.service';
import CreateProductModal from 'shared/components/CreateProductModal';
import COPagination from 'shared/components/COPagination';
import COSearch from 'shared/components/COSearch';
import { SearchProps } from 'enums/ProductsViewEnums';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import ProductListTable, { FetchProductProps } from './ProductListTable';
import { ProcessingFacility } from 'models/ProcessingFacility/ProcessingFacility.model';
import ProductSiblingService from 'services/ProductSiblingsService';
import useFilters from 'shared/hooks/useFilters';
import { ProductParams } from 'constants/allProductsTable.constants';
import { SortingTypesEnums } from 'enums/sortingTypes.enum';
import { AllProductListTableFields } from 'enums/AllProductListEnums';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';

export interface ProductsProps {
  processingFacilityList: ProcessingFacility[];
  handleModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const AllProducts: FC<ProductsProps> = ({
  processingFacilityList,
  handleModalOpen,
}) => {
  const { loading } = ProductsService();
  const { organizationData } = useParentOrganizationContext();
  const {
    loading: productListLoading,
    fetchProductList,
    parentProducts,
    parentProductsPaginationMeta,
  } = ProductSiblingService();
  const user = localStorage.getItem('userDetails');
  let parsedUser;
  if (user !== null) {
    parsedUser = JSON.parse(user);
  }
  const { getParams, setParams } = useFilters();
  const initialParams = getParams(ProductParams);
  const initialQueryParams = {
    page: initialParams.page ? Number(initialParams.page) : 1,
    sortBy: initialParams.sortBy ?? undefined,
    sortDir: initialParams.sortDir ?? undefined,
    search: initialParams.search ?? undefined,
  };
  const [createProductModalState, setCreateProductModalState] =
    useState<boolean>(false);
  const [filterParams, setFilterParams] =
    useState<FetchProductProps>(initialQueryParams);
  const updateFilters = (params?: FetchProductProps) =>
    setFilterParams(prev => {
      return { ...prev, ...params };
    });
  const handleQueryParams = () => {
    const modifiedParams = {
      ...filterParams,
      sortDir:
        filterParams.sortDir === SortingTypesEnums.ASCEND
          ? AllProductListTableFields.ASC
          : filterParams.sortDir === SortingTypesEnums.DESCEND
          ? AllProductListTableFields.DESC
          : undefined,
    };
    fetchProductList(modifiedParams);
    setParams(filterParams ?? {});
  };

  useEffect(() => {
    handleQueryParams();
  }, [filterParams]);

  const handleCloseCreateProductModal = () => {
    setCreateProductModalState(false);
  };
  const handleOpenCreateProductModal = () => {
    if (
      processingFacilityList?.length &&
      organizationData?.organization?.name
    ) {
      setCreateProductModalState(true);
    } else {
      handleModalOpen?.(true);
    }
  };

  const handlePaginationChange = (page: number) => updateFilters({ page });

  const handleInputChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event?.target?.value;
    updateFilters({ search: searchValue ? searchValue : undefined });
  };
  return (
    <>
      <div className={styles.searchContainer}>
        <COSearch
          name={SearchProps.SEARCH_NAME}
          placeholderString={SearchProps.PLACEHOLDER_STRING}
          inputValue={filterParams?.search}
          handleChange={handleInputChange}
        />
      </div>
      <div className={styles.PaginationContainer}>
        <COPagination
          onChange={handlePaginationChange}
          total={parentProductsPaginationMeta?.totalItems ?? 0}
          current={parentProductsPaginationMeta?.currentPage}
          pageSize={parentProductsPaginationMeta?.pageSize ?? 10}
        />
      </div>

      {loading ? (
        <div className={styles.loaderContainer}>
          <LoaderAnimation />
        </div>
      ) : (
        <div className={styles.allProductsContainer}>
          {createProductModalState && (
            <CreateProductModal
              showModalState={createProductModalState}
              handleModalCancel={handleCloseCreateProductModal}
              handleModalConfirm={handleCloseCreateProductModal}
              userId={Number(parsedUser.id)}
              isCreate={true}
            />
          )}
          <ProductListTable
            filterParams={filterParams}
            updateFilters={updateFilters}
            loading={productListLoading}
            parentProducts={parentProducts}
            handleOpenCreateProductModal={handleOpenCreateProductModal}
          />
        </div>
      )}
    </>
  );
};

export default AllProducts;
